import type {
  CreateUpstreamRequest,
  DiscoverUpstreamCapabilitiesRequest,
  ProbeRequest,
  ProbeResponse,
  ProbeResultRecord,
  RouteBindingRecord,
  SetUpstreamChannelCostsRequest,
  UpdateUpstreamRequest,
  UpstreamChannelCostRecord,
  UpstreamModelCapabilityRecord,
  UpstreamCatalogResponse,
  UpstreamConfig,
  UpstreamRetirementImpactResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createUpstream,
  createRouteBinding,
  deleteUpstream,
  deleteRouteBinding,
  discoverUpstreamCapabilities,
  getUpstream,
  getUpstreamRetirementImpact,
  ignoreUpstreamCapability,
  listUpstreamChannelCosts,
  listCatalog,
  listProbeResults,
  listRouteBindings,
  listUpstreamCapabilities,
  listUpstreams,
  probeUpstreamCapability,
  probeUpstream,
  setUpstreamChannelCosts,
  unignoreUpstreamCapability,
  updateRouteBinding,
  updateUpstream,
} from './upstreams.ts';

/**
 * React Query wrappers for the upstreams API.
 *
 * Query key conventions:
 *   - `['upstreams']`            — list query
 *   - `['upstreams', id]`        — single upstream query
 *   - `['upstreams', 'catalog']` — global catalog (per-upstream model map)
 *
 * Mutation invalidation map:
 *   - create / update / delete → invalidate `['upstreams']` + specific id +
 *     catalog. Delete also marks the specific id stale so the detail page
 *     re-fetches and sees soft-delete state.
 *   - model mapping mutations live in `useModelMappings.ts`; they invalidate
 *     their dedicated mapping query and any callers can refresh catalog when
 *     they need the global view.
 *
 * The hooks read `fetchImpl` from the session context; tests inject a
 * stub there (same pattern as Wave 5A).
 */

export const UPSTREAMS_QUERY_KEY = ['upstreams'] as const;
export const UPSTREAMS_CATALOG_QUERY_KEY = ['upstreams', 'catalog'] as const;

export function upstreamQueryKey(id: string): readonly ['upstreams', string] {
  return ['upstreams', id] as const;
}

export function upstreamChannelCostsQueryKey(id: string): readonly ['upstreams', string, 'channel-costs'] {
  return ['upstreams', id, 'channel-costs'] as const;
}

export function useUpstreamsList(): UseQueryResult<UpstreamConfig[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: UPSTREAMS_QUERY_KEY,
    queryFn: ({ signal }) => listUpstreams(fetchImpl, signal),
  });
}

export function useUpstream(id: string | undefined): UseQueryResult<UpstreamConfig> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamQueryKey(id ?? ''),
    queryFn: ({ signal }) => {
      if (id === undefined || id.length === 0) {
        throw new Error('useUpstream: id is required');
      }
      return getUpstream(id, fetchImpl, signal);
    },
    enabled: id !== undefined && id.length > 0,
  });
}

export function useUpstreamChannelCosts(
  id: string,
): UseQueryResult<UpstreamChannelCostRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamChannelCostsQueryKey(id),
    queryFn: ({ signal }) => listUpstreamChannelCosts(id, fetchImpl, signal),
    enabled: id.length > 0,
  });
}

export function useSetUpstreamChannelCosts(
  id: string,
): UseMutationResult<UpstreamChannelCostRecord[], Error, SetUpstreamChannelCostsRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => setUpstreamChannelCosts(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamChannelCostsQueryKey(id) });
    },
  });
}

export function useUpstreamCatalog(): UseQueryResult<UpstreamCatalogResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: UPSTREAMS_CATALOG_QUERY_KEY,
    queryFn: ({ signal }) => listCatalog(fetchImpl, signal),
  });
}

/**
 * Retirement-impact summary consumed by the DangerZone confirmation
 * dialog. `enabled` is surfaced so the caller can gate the fetch on
 * dialog open (we don't want to query on every UpstreamDetail mount
 * — the data is only relevant when the operator is about to delete).
 */
export function useUpstreamRetirementImpact(
  id: string | undefined,
  options: { readonly enabled: boolean } = { enabled: true },
): UseQueryResult<UpstreamRetirementImpactResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['upstreams', id ?? '', 'retirement-impact'] as const,
    queryFn: ({ signal }) => {
      if (id === undefined || id.length === 0) {
        throw new Error('useUpstreamRetirementImpact: id is required');
      }
      return getUpstreamRetirementImpact(id, fetchImpl, signal);
    },
    enabled: options.enabled && id !== undefined && id.length > 0,
    // Retirement impact is safety-critical: the operator is about to
    // soft-delete an upstream based on what this dialog shows. A
    // concurrent attach / detach / peer deletion must not be masked
    // by a cached snapshot. `staleTime: 0` + `refetchOnMount: 'always'`
    // means each dialog open re-reads from the backend; the query is
    // three indexed D1 reads so the cost is negligible.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCreateUpstream(): UseMutationResult<
  UpstreamConfig,
  Error,
  CreateUpstreamRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createUpstream(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useUpdateUpstream(
  id: string,
): UseMutationResult<UpstreamConfig, Error, UpdateUpstreamRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateUpstream(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: upstreamQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useDeleteUpstream(
  id: string,
): UseMutationResult<void, Error, void> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await deleteUpstream(id, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: upstreamQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export interface ProbeVars {
  body: ProbeRequest;
  signal?: AbortSignal;
}

export function useProbeUpstream(
  id: string,
): UseMutationResult<ProbeResponse, Error, ProbeVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, signal }) => probeUpstream(id, body, fetchImpl, signal),
    onSuccess: () => {
      // Refresh the probe history card so the new outcome appears at the top.
      void qc.invalidateQueries({ queryKey: probeResultsQueryKey(id) });
    },
  });
}

export function probeResultsQueryKey(id: string): readonly ['upstreams', string, 'probes'] {
  return ['upstreams', id, 'probes'] as const;
}

export function upstreamCapabilitiesQueryKey(id: string): readonly ['upstreams', string, 'capabilities'] {
  return ['upstreams', id, 'capabilities'] as const;
}

export function routeBindingsQueryKey(id: string): readonly ['upstreams', string, 'route-bindings'] {
  return ['upstreams', id, 'route-bindings'] as const;
}

export function useProbeResults(
  id: string,
): UseQueryResult<ProbeResultRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: probeResultsQueryKey(id),
    queryFn: ({ signal }) => listProbeResults(id, fetchImpl, signal),
  });
}

export function useUpstreamCapabilities(
  id: string,
): UseQueryResult<UpstreamModelCapabilityRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamCapabilitiesQueryKey(id),
    queryFn: ({ signal }) => listUpstreamCapabilities(id, fetchImpl, signal),
    enabled: id.length > 0,
  });
}

export function useRouteBindings(
  id: string,
): UseQueryResult<RouteBindingRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: routeBindingsQueryKey(id),
    queryFn: ({ signal }) => listRouteBindings(id, fetchImpl, signal),
    enabled: id.length > 0,
  });
}

export function useDiscoverUpstreamCapabilities(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  DiscoverUpstreamCapabilitiesRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await discoverUpstreamCapabilities(id, body, fetchImpl);
      return [...res.capabilities];
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useProbeUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await probeUpstreamCapability(id, capabilityId, fetchImpl);
      return res.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useIgnoreUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await ignoreUpstreamCapability(id, capabilityId, fetchImpl);
      return res.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useUnignoreUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await unignoreUpstreamCapability(id, capabilityId, fetchImpl);
      return res.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useCreateRouteBinding(
  id: string,
): UseMutationResult<RouteBindingRecord, Error, { capability_id: string; enabled?: boolean; priority?: number }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createRouteBinding(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useUpdateRouteBinding(
  id: string,
): UseMutationResult<RouteBindingRecord, Error, { binding_id: string; enabled?: boolean; priority?: number }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ binding_id, ...body }) => updateRouteBinding(id, binding_id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useDeleteRouteBinding(
  id: string,
): UseMutationResult<void, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bindingId) => {
      await deleteRouteBinding(id, bindingId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routeBindingsQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}
